const PHONE_CONTACT_TYPES = new Set(['phone', 'viber', 'whatsapp']);
const TEXT_CONTACT_TYPES = new Set(['vk', 'telegram', 'max']);

function placeholderToPhoneMask(placeholder) {
  const mask = placeholder.replace(/\d/g, '_');

  return mask.startsWith('+') ? mask : `+${mask}`;
}

function placeholderToTextPrefix(placeholder) {
  const slashIndex = placeholder.indexOf('/');

  if (slashIndex === -1) {
    return placeholder;
  }

  return placeholder.slice(0, slashIndex + 1);
}

function isPhoneContactType(type) {
  return PHONE_CONTACT_TYPES.has(type);
}

function isTextContactType(type) {
  return TEXT_CONTACT_TYPES.has(type);
}

function isMaskedContactType(type) {
  return isPhoneContactType(type) || isTextContactType(type);
}

function isMaskedInput(input) {
  return input.classList.contains('organizer_contact_phone_mask')
    || input.classList.contains('organizer_contact_text_mask');
}

function stripTextContactPrefix(value, prefix) {
  let variable = String(value || '')
    .replace(/^(https?:\/\/)?(www\.)?(vk\.com\/|t\.me\/|max\.ru\/)/i, '');

  if (variable.startsWith(prefix)) {
    variable = variable.slice(prefix.length);
  }

  return variable.replace(/[^a-zA-Z0-9_.-]/g, '');
}

class OrganizerContactTextMask {
  constructor(input, prefix) {
    this.input = input;
    this.prefix = prefix;

    this.onInput = () => this.applyMask();
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onClick = () => this.ensureCursorAfterPrefix();
    this.onFocus = () => this.ensureCursorAfterPrefix();

    this.attachInputHandlers();
    this.initializeMask();
  }

  attachInputHandlers() {
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('click', this.onClick);
    this.input.addEventListener('focus', this.onFocus);
  }

  ensureCursorAfterPrefix() {
    const selectionStart = this.input.selectionStart ?? 0;

    if (selectionStart < this.prefix.length) {
      this.input.setSelectionRange(this.prefix.length, this.prefix.length);
    }
  }

  initializeMask() {
    const suffix = this.input.value.trim()
      ? stripTextContactPrefix(this.input.value, this.prefix)
      : '';

    this.input.value = this.prefix + suffix;
    this.input.setSelectionRange(this.input.value.length, this.input.value.length);
  }

  handleKeydown(event) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End'];

    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Backspace' && this.input.selectionStart <= this.prefix.length) {
        event.preventDefault();
      }

      if (event.key === 'Delete' && this.input.selectionStart < this.prefix.length) {
        event.preventDefault();
      }

      return;
    }

    if (this.input.selectionStart < this.prefix.length) {
      event.preventDefault();
      this.input.setSelectionRange(this.prefix.length, this.prefix.length);
      return;
    }

    if (!/[\w.-]/.test(event.key)) {
      event.preventDefault();
    }
  }

  applyMask() {
    const suffix = stripTextContactPrefix(this.input.value, this.prefix);
    this.input.value = this.prefix + suffix;

    const cursorPos = Math.max(this.prefix.length + suffix.length, this.prefix.length);
    this.input.setSelectionRange(cursorPos, cursorPos);
  }
}

function destroyOrganizerContactMask(input) {
  const wrapper = input.closest('.phone_mask_div');

  if (wrapper) {
    wrapper.parentNode.insertBefore(input, wrapper);
    wrapper.remove();
  }

  input.classList.remove(
    'organizer_contact_phone_mask',
    'phone_mask_country_locked',
    'phone_input',
    'organizer_contact_text_mask',
    'justify-start',
    'items-start',
  );
  delete input.dataset.mask;

  const cleanInput = input.cloneNode(true);
  input.parentNode.replaceChild(cleanInput, input);

  return cleanInput;
}

function applyOrganizerContactMask(input, type, placeholder) {
  if (isPhoneContactType(type)) {
    input.dataset.mask = placeholderToPhoneMask(placeholder);
    input.classList.add('organizer_contact_phone_mask', 'phone_mask_country_locked');

    // eslint-disable-next-line no-undef
    new PhoneMask(input);

    if (input.value.trim()) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    return input;
  }

  if (isTextContactType(type)) {
    const prefix = placeholderToTextPrefix(placeholder);

    input.dataset.mask = prefix;
    input.classList.add('organizer_contact_text_mask');
    new OrganizerContactTextMask(input, prefix);

    return input;
  }

  return input;
}

function updateOrganizerContactInput(contactSelect, isInitial = false) {
  const currentOption = contactSelect.options[contactSelect.selectedIndex];
  const type = currentOption.value;
  const dataPlaceholder = currentOption.getAttribute('data-placeholder');
  const parent = contactSelect.parentNode;
  let contactInput = parent.querySelector('input[name="extra_contact[value]"]');

  if (!contactInput) {
    return;
  }

  contactInput.placeholder = dataPlaceholder;

  if (!isInitial) {
    contactInput.value = '';
  }

  if (isMaskedContactType(type)) {
    if (isMaskedInput(contactInput)) {
      contactInput = destroyOrganizerContactMask(contactInput);
    }

    if (!isInitial) {
      contactInput.value = '';
    }

    applyOrganizerContactMask(contactInput, type, dataPlaceholder);
    return;
  }

  if (isMaskedInput(contactInput)) {
    destroyOrganizerContactMask(contactInput);
  }
}

export default function initOrganizerContactsSelect() {
  const contactSelect = document.querySelector('.form__input.organizer_contact_type_select');

  if (!contactSelect) {
    return;
  }

  updateOrganizerContactInput(contactSelect, true);

  contactSelect.addEventListener('change', () => {
    updateOrganizerContactInput(contactSelect, false);
  });
}
